import React, { useMemo, useCallback } from "react";
import { TableRow, TableCell, Box, Typography, Chip, useTheme } from "@mui/material";
import PickTrendChart from "./PickTrendChart";
import { getPlayerPickChartData } from "../helpers/playerHelpers";
import { teamColorBg } from "../helpers/colorHelpers";
import { semantic, surfaceTokens, getPlatformColor, getPlatformShortLabel } from "../config/theme";
import { PositionChip, TeamBadge, ExposureBar, DeltaPill, PlatformChip } from "./ui";
import type { PlayerPlatformStat } from "../helpers/platformSplit";
import type { MarketSeries } from "../helpers/marketSeries";
import type { MarketLine } from "./PickTrendChart";
import type { Player } from "../types/draft";
import type { ComboCacheEntry } from "../context/DraftDataContext";

interface PlayerRowProps {
  player: Player;
  myAdp: React.ReactNode;
  udAdp: React.ReactNode;
  clv: React.ReactNode;
  clvPct: React.ReactNode;
  exposure: number | string;
  count: number;
  isOpen: boolean;
  onClick: () => void;
  onToggle?: (key: string) => void;
  playerKey?: string;
  TEAM_COLORS: Record<string, string>;
  POSITION_COLORS: Record<string, string>;
  allPlayers: Player[];
  visibleColumns: Set<string> | null;
  playersByDraftIndex?: Map<string, Player[]>;
  totalDrafts?: number;
  rowIndex?: number;
  combosCache?: Map<string, ComboCacheEntry>;
  /**
   * This player's market, one entry per platform he was drafted on (matched
   * upstream by name + position). One line per board rather than a single
   * majority-vote line, so a mixed player's picks are each scored against the
   * market they were actually made in.
   */
  marketSeries?: MarketSeries[];
  /** When true, renders only the expanded detail box (no TableRow wrappers). Used in mobile card view. */
  expandedOnly?: boolean;
  /** Portfolio-average exposure — turns the exposure bar diverging (over/under vs avg). */
  avgExposure?: number;
  /**
   * Platforms to split the market-relative metrics across. Empty/absent for a
   * single-platform portfolio, which renders exactly as it always has.
   */
  splitProviders?: string[];
  /** This player's per-platform numbers, in `splitProviders` order. */
  platformStats?: PlayerPlatformStat[];
}

/** Metrics that render one cell per platform in a mixed portfolio. Mirrors
 *  SPLIT_METRICS in MyPlayersPage, which builds the matching column headers. */
const SPLIT_METRIC_IDS = ["exposure", "myAdp", "udAdp", "clv", "clvPct"] as const;

/** One number in a split cell — em dash when the platform has no data. */
function fmtStat(v: number | null, digits = 1): string {
  return v === null || isNaN(v) ? "\u2014" : v.toFixed(digits);
}

// Compact legend swatch for the pick-trend chart series. "diamond" mirrors the
// chart's second-platform marker — the chart encodes platform as shape because
// the marker's FILL is already carrying the good/bad CLV tone.
function LegendSwatch({ kind, color, label }: { kind: "dot" | "diamond" | "line" | "dash"; color: string; label: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {kind === "dot" ? (
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }} />
      ) : kind === "diamond" ? (
        <Box sx={{ width: 8, height: 8, bgcolor: color, transform: "rotate(45deg)" }} />
      ) : (
        <Box sx={{ width: 14, borderTop: `2px ${kind === "dash" ? "dashed" : "solid"} ${color}`, opacity: 0.5 }} />
      )}
      <Typography sx={{ fontSize: 11, color: "text.secondary", whiteSpace: "nowrap" }}>{label}</Typography>
    </Box>
  );
}

function PlayerRow({
  player,
  myAdp,
  udAdp,
  clv,
  clvPct,
  exposure,
  count,
  isOpen,
  onClick,
  onToggle,
  playerKey,
  TEAM_COLORS,
  POSITION_COLORS,
  allPlayers,
  visibleColumns,
  playersByDraftIndex,
  totalDrafts = 0,
  rowIndex = 0,
  combosCache,
  marketSeries,
  expandedOnly = false,
  avgExposure,
  splitProviders,
  platformStats,
}: PlayerRowProps) {
  const { palette } = useTheme();
  const isDark = palette.mode === "dark";

  // Stable click handler: prefer onToggle(playerKey) over onClick
  const handleClick = useCallback(() => {
    if (onToggle && playerKey) {
      onToggle(playerKey);
    } else {
      onClick();
    }
  }, [onToggle, playerKey, onClick]);

  // Read combos from the pre-computed cache (populated at CSV load time).
  const cacheKey = `${player.name}|${player.team}|${player.position}`;
  const cachedCombos = combosCache?.get(cacheKey);
  const top2Combos = cachedCombos?.top2 ?? [];
  const top3Combos = cachedCombos?.top3 ?? [];

  // Only compute chart data when the row is expanded.
  // Shared window bounds drive both the pick line and the ADP trend overlay.
  const pickWindow = useMemo(() => {
    if (!isOpen) return null;
    const raw = getPlayerPickChartData(player);
    if (raw.length === 0) return null;
    const xMin = raw[0].date - 86400000;
    const xMax = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); })();
    return { raw, xMin, xMax };
  }, [isOpen, player]);

  const pickChartData = useMemo(() => {
    if (!pickWindow) return [];
    const { raw, xMin, xMax } = pickWindow;
    const result = [...raw];
    if (result[0].date > xMin) {
      result.unshift({ pick: result[0].pick, date: xMin, dateObj: new Date(xMin), dateStr: "", _ghost: true });
    }
    if (result[result.length - 1].date < xMax) {
      result.push({ pick: result[result.length - 1].pick, date: xMax, dateObj: new Date(xMax), dateStr: "", _ghost: true });
    }
    return result;
  }, [pickWindow]);

  // Market ADP over time per platform, clipped to the chart's date window.
  // Each line keeps its platform accent so the chart, the legend and the split
  // columns above all name the same two boards the same way.
  const markets = useMemo<MarketLine[]>(() => {
    if (!pickWindow || !marketSeries || marketSeries.length === 0) return [];
    const { xMin, xMax } = pickWindow;
    return marketSeries.map(s => ({
      provider: s.provider,
      label: getPlatformShortLabel(s.provider),
      color: getPlatformColor(s.provider, isDark),
      current: s.current,
      points: s.points
        .map(h => ({ date: new Date(h.date).getTime(), adp: h.adp }))
        .filter(p => !isNaN(p.date) && p.date >= xMin && p.date <= xMax)
        .sort((a, b) => a.date - b.date),
    }));
  }, [pickWindow, marketSeries, isDark]);

  // Lines with enough in-window points to actually draw — what the legend lists.
  const drawnMarkets = useMemo(() => markets.filter(m => m.points.length >= 2), [markets]);

  // Platforms among this player's picks; two means the markers carry shapes.
  const pickPlatforms = useMemo(() => {
    const seen = new Set<string>();
    for (const pt of pickChartData) if (!pt._ghost && pt.provider) seen.add(pt.provider);
    const ordered = markets.map(m => m.provider).filter(p => seen.has(p));
    for (const p of seen) if (!ordered.includes(p)) ordered.push(p);
    return ordered;
  }, [pickChartData, markets]);

  // Parse numeric values for visualizations
  const exposurePct = typeof exposure === "string" ? parseFloat(exposure) : Number(exposure);
  const clvNum = typeof clv === "string" ? parseFloat(clv as string) : null;
  const clvPctNum = typeof clvPct === "string" ? parseFloat(clvPct as string) : null;
  const posColor = POSITION_COLORS[player.position] || "#888";
  // Combo-frequency bars: brandLight reads on both surfaces (muddy dark green
  // #3a6b30 was low-contrast on the dark paper).
  const primaryColor = surfaceTokens(isDark).brandLight;

  // UD ADP numeric value for reference line
  const udAdpNum = typeof udAdp === "number" ? udAdp : (typeof udAdp === "string" ? parseFloat(udAdp as string) : null);
  const udAdpForRef = udAdpNum && !isNaN(udAdpNum) ? udAdpNum : null;

  // Helper to render combo percentage
  const renderComboPct = (comboCount: number): string =>
    count > 0 ? `${Math.round((comboCount / count) * 100)}%` : "0%";

  const renderComboPctNum = (comboCount: number): number =>
    count > 0 ? Math.round((comboCount / count) * 100) : 0;

  // Split mode: the market-relative metrics render one cell per platform.
  const isSplit = (splitProviders?.length ?? 0) > 1 && !!platformStats;
  const statFor = (prov: string) => platformStats?.find(st => st.provider === prov);
  // Split metrics occupy one cell PER PLATFORM, so the detail row has to span
  // more than the visible-column count or it tears the table layout.
  const splitCols = isSplit
    ? [...SPLIT_METRIC_IDS].filter(id => !visibleColumns || visibleColumns.has(id)).length *
      (splitProviders!.length - 1)
    : 0;
  const expandedColSpan = 1 + (visibleColumns ? visibleColumns.size : 9) + splitCols;

  // Helper to get team color for a player (fallback to position color)
  const getChipColor = (team: string, position: string) => ({
    backgroundColor: teamColorBg(TEAM_COLORS[team] || POSITION_COLORS[position] || "#bbb", isDark),
    color: "#fff",
    fontWeight: 700,
    fontSize: "0.75em",
  });

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: "pointer", ...(expandedOnly ? { display: "none" } : {}) }}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
        tabIndex={0}
        aria-expanded={isOpen}
        aria-label={`${isOpen ? "Collapse" : "Expand"} details for ${player.name}`}
        role="button"
      >
        <TableCell sx={{ width: 40, p: 0, pl: 1 }}>
          <span
            aria-hidden="true"
            style={{ fontWeight: 700 }}
          >
            {isOpen ? "▼" : "▶"}
          </span>
        </TableCell>
        {(!visibleColumns || visibleColumns.has("name")) && (
          <TableCell>{player.name}</TableCell>
        )}
        {(!visibleColumns || visibleColumns.has("team")) && (
          <TableCell>
            <TeamBadge team={player.team} size="sm" />
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.has("position")) && (
          <TableCell>
            <PositionChip position={player.position} />
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.has("exposure")) && (
          isSplit
            ? splitProviders!.map(prov => {
                const st = statFor(prov);
                // Each platform's own denominator: `drafts / that platform's
                // drafts`. The diverging-vs-average mode is deliberately off
                // here — the portfolio average spans both boards and would be
                // the wrong reference for a single platform's lane.
                return (
                  <TableCell key={`exposure:${prov}`} sx={{ px: 1 }}>
                    {st ? (
                      <ExposureBar
                        value={st.exposure ?? 0}
                        width={34}
                        color={getPlatformColor(prov, isDark)}
                      />
                    ) : "\u2014"}
                  </TableCell>
                );
              })
            : (
              <TableCell>
                <ExposureBar value={isNaN(exposurePct) ? 0 : exposurePct} center={avgExposure} width={96} />
              </TableCell>
            )
        )}
        {(!visibleColumns || visibleColumns.has("count")) && (
          <TableCell>{count}</TableCell>
        )}
        {(!visibleColumns || visibleColumns.has("myAdp")) && (
          isSplit
            ? splitProviders!.map(prov => (
                <TableCell key={`myAdp:${prov}`} sx={{ px: 1, fontVariantNumeric: "tabular-nums" }}>
                  {fmtStat(statFor(prov)?.myAdp ?? null, 1)}
                </TableCell>
              ))
            : <TableCell>{myAdp}</TableCell>
        )}
        {(!visibleColumns || visibleColumns.has("udAdp")) && (
          isSplit
            ? splitProviders!.map(prov => (
                <TableCell key={`udAdp:${prov}`} sx={{ px: 1, fontVariantNumeric: "tabular-nums" }}>
                  {fmtStat(statFor(prov)?.marketAdp ?? null, 1)}
                </TableCell>
              ))
            : <TableCell>{udAdp}</TableCell>
        )}
        {(!visibleColumns || visibleColumns.has("clv")) && (
          isSplit
            ? splitProviders!.map(prov => {
                const v = statFor(prov)?.clv ?? null;
                return (
                  <TableCell key={`clv:${prov}`} sx={{ px: 1 }}>
                    <DeltaPill value={v} display={v === null ? undefined : v.toFixed(1)} />
                  </TableCell>
                );
              })
            : (
              <TableCell>
                {typeof clv === "string" && clv !== "-" ? (
                  <DeltaPill value={clvNum} display={clv} />
                ) : clv}
              </TableCell>
            )
        )}
        {(!visibleColumns || visibleColumns.has("clvPct")) && (
          isSplit
            ? splitProviders!.map(prov => {
                const v = statFor(prov)?.clvPct ?? null;
                return (
                  <TableCell key={`clvPct:${prov}`} sx={{ px: 1 }}>
                    <DeltaPill value={v} display={v === null ? undefined : `${v.toFixed(1)}%`} />
                  </TableCell>
                );
              })
            : (
              <TableCell>
                {typeof clvPct === "string" && clvPct !== "-" ? (
                  <DeltaPill value={clvPctNum} display={`${clvPct}%`} />
                ) : clvPct}
              </TableCell>
            )
        )}
      </TableRow>
      {isOpen && (
        <TableRow>
          <TableCell colSpan={expandedColSpan} sx={{ p: 0, border: "none" }}>
            <Box
              sx={{
                bgcolor: "background.paper",
                borderTop: `3px solid ${posColor}`,
                borderBottom: "1px solid",
                borderColor: "divider",
                p: { xs: 2, md: 3 },
                animation: "fadeIn 0.2s ease-out",
                "@keyframes fadeIn": { from: { opacity: 0, transform: "translateY(-4px)" }, to: { opacity: 1, transform: "translateY(0)" } },
              }}
            >
              {/* Platform breakdown — the full per-market picture, including the
                  draft counts behind each exposure percentage. Only rendered for
                  a mixed portfolio; a single platform has nothing to compare. */}
              {isSplit && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="subtitle2" fontWeight={700} mb={1} color="text.secondary">
                    Platform breakdown
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "auto repeat(5, minmax(0, 1fr))",
                      gap: "2px 12px",
                      maxWidth: 560,
                      alignItems: "center",
                    }}
                  >
                    {["", "Drafts", "Exposure", "My ADP", "Market ADP", "CLV"].map((h, i) => (
                      <Typography
                        key={h || `h${i}`}
                        sx={{ fontSize: 10.5, fontWeight: 600, color: "text.secondary",
                          textTransform: "uppercase", letterSpacing: 0.4, pb: 0.5 }}
                      >
                        {h}
                      </Typography>
                    ))}
                    {splitProviders!.map(prov => {
                      const st = statFor(prov);
                      return (
                        <React.Fragment key={prov}>
                          <Box sx={{ py: 0.375 }}>
                            <PlatformChip provider={prov} size="sm" />
                          </Box>
                          <Typography sx={{ fontSize: 12.5, fontVariantNumeric: "tabular-nums" }}>
                            {st ? `${st.drafts} / ${st.totalDrafts}` : "\u2014"}
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, fontVariantNumeric: "tabular-nums",
                            fontWeight: 600, color: getPlatformColor(prov, isDark) }}>
                            {st?.exposure != null ? `${st.exposure.toFixed(1)}%` : "\u2014"}
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, fontVariantNumeric: "tabular-nums" }}>
                            {fmtStat(st?.myAdp ?? null)}
                          </Typography>
                          <Typography sx={{ fontSize: 12.5, fontVariantNumeric: "tabular-nums" }}>
                            {fmtStat(st?.marketAdp ?? null)}
                          </Typography>
                          <Box sx={{ fontSize: 12.5 }}>
                            <DeltaPill
                              value={st?.clv ?? null}
                              display={st?.clv != null ? st.clv.toFixed(1) : undefined}
                            />
                          </Box>
                        </React.Fragment>
                      );
                    })}
                  </Box>
                  <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 1 }}>
                    Each platform is scored against its own board — Underdog runs 18 rounds, DraftKings 20 —
                    so exposure and CLV are never blended across the two.
                  </Typography>
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 3,
                  alignItems: "flex-start",
                }}
              >
                {/* Left column: Chart */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={700} mb={0.5} color="text.secondary">
                    Pick Trend
                  </Typography>
                  {pickChartData.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1, alignItems: "center" }}>
                      {/* With two platforms in play the picks are told apart by
                          SHAPE, so the legend names each one; a single-platform
                          player keeps the plain "Your picks" dot. */}
                      {pickPlatforms.length > 1 ? (
                        pickPlatforms.map((prov, i) => (
                          <LegendSwatch
                            key={prov}
                            kind={i === 1 ? "diamond" : "dot"}
                            color={posColor}
                            label={`Your ${getPlatformShortLabel(prov)} picks`}
                          />
                        ))
                      ) : (
                        <LegendSwatch kind="dot" color={posColor} label="Your picks" />
                      )}
                      {drawnMarkets.map(m => (
                        <LegendSwatch
                          key={m.provider}
                          kind="line"
                          color={m.color}
                          label={drawnMarkets.length > 1 ? `${m.label} market ADP` : "Market ADP"}
                        />
                      ))}
                      {udAdpForRef != null && drawnMarkets.length === 0 && (
                        <LegendSwatch kind="dash" color={semantic.neutral} label={`Current ADP ${udAdpForRef}`} />
                      )}
                    </Box>
                  )}
                  <PickTrendChart
                    data={pickChartData}
                    color={posColor}
                    udAdp={udAdpForRef}
                    markets={markets}
                  />
                </Box>

                {/* Right column: Combo sections */}
                <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
                  {/* 2-Player Combos */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} mb={1} color="text.secondary">
                      Top 2-Player Combos
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {top2Combos.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No combos.</Typography>
                      ) : (
                        top2Combos.map((combo, idx) => {
                          const pct = renderComboPctNum(combo.count);
                          return (
                            <Box key={idx}>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                                <Chip
                                  label={`${combo.players[0].name} (${combo.players[0].team} ${combo.players[0].position})`}
                                  size="small"
                                  sx={{
                                    ...getChipColor(combo.players[0].team, combo.players[0].position),
                                    borderRadius: "6px",
                                    height: 22,
                                    maxWidth: 200,
                                  }}
                                />
                                <Typography variant="caption" fontWeight={600} sx={{ ml: 1, flexShrink: 0 }}>
                                  {renderComboPct(combo.count)}
                                </Typography>
                              </Box>
                              {/* Frequency bar */}
                              <Box sx={{ height: 4, bgcolor: `${primaryColor}20`, borderRadius: 1, mx: 0.5 }}>
                                <Box sx={{
                                  height: "100%",
                                  width: `${pct}%`,
                                  bgcolor: primaryColor,
                                  borderRadius: 1,
                                  transition: "width 0.3s",
                                }} />
                              </Box>
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  </Box>

                  {/* 3-Player Combos */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} mb={1} color="text.secondary">
                      Top 3-Player Combos
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {top3Combos.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No combos.</Typography>
                      ) : (
                        top3Combos.map((combo, idx) => {
                          const pct = renderComboPctNum(combo.count);
                          return (
                            <Box key={idx}>
                              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
                                {combo.players.map(p => (
                                  <Chip
                                    key={p.name + p.team}
                                    label={`${p.name} (${p.team} ${p.position})`}
                                    size="small"
                                    sx={{
                                      ...getChipColor(p.team, p.position),
                                      borderRadius: "6px",
                                      height: 22,
                                    }}
                                  />
                                ))}
                                <Typography variant="caption" fontWeight={600} sx={{ ml: "auto" }}>
                                  {renderComboPct(combo.count)}
                                </Typography>
                              </Box>
                              <Box sx={{ height: 4, bgcolor: `${primaryColor}20`, borderRadius: 1, mx: 0.5 }}>
                                <Box sx={{
                                  height: "100%",
                                  width: `${pct}%`,
                                  bgcolor: primaryColor,
                                  borderRadius: 1,
                                }} />
                              </Box>
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// Separate export for the expanded detail content used in mobile card view
export function PlayerExpandedDetail(props: Omit<React.ComponentProps<typeof PlayerRow>, 'expandedOnly'>) {
  return <PlayerRow {...props} expandedOnly isOpen />;
}

export default React.memo(PlayerRow, (prev, next) => {
  return (
    prev.isOpen === next.isOpen &&
    prev.myAdp === next.myAdp &&
    prev.udAdp === next.udAdp &&
    prev.clv === next.clv &&
    prev.clvPct === next.clvPct &&
    prev.exposure === next.exposure &&
    prev.avgExposure === next.avgExposure &&
    prev.count === next.count &&
    prev.player === next.player &&
    prev.marketSeries === next.marketSeries &&
    prev.allPlayers === next.allPlayers &&
    prev.visibleColumns === next.visibleColumns &&
    prev.playersByDraftIndex === next.playersByDraftIndex &&
    prev.totalDrafts === next.totalDrafts &&
    prev.onToggle === next.onToggle &&
    prev.playerKey === next.playerKey &&
    prev.platformStats === next.platformStats &&
    prev.splitProviders === next.splitProviders
  );
});